import React, { useCallback, useMemo } from "react";
import ReportTable from "../ReportTable/ReportTable";
import utils from "../../../utils/utils";
import { isDocumentMasterRowUsed } from "./documentMasterUtils";
import "./DocumentMasterReportTable.scss";

export function buildDocumentMasterFieldOptions(nameField, nameLabel) {
  return [
    { key: nameField, label: nameLabel },
    { key: "Abberiviation", label: "Abbreviation" },
    { key: "SystemDateTime", label: "Creation Date & Time" },
    { key: "CreatedBy", label: "Created By" },
    { key: "Status", label: "Status" },
  ];
}

export function buildDocumentMasterDefaultColumns(nameField) {
  return [nameField, "Abberiviation", "SystemDateTime", "CreatedBy", "Status"];
}

const DocumentMasterReportTable = ({
  rows = [],
  nameField,
  nameColumnLabel,
  layoutStorageKey = "",
  onEdit,
  onDelete,
  isRowUsed = isDocumentMasterRowUsed,
  loading = false,
}) => {
  const fieldOptions = useMemo(
    () => buildDocumentMasterFieldOptions(nameField, nameColumnLabel),
    [nameField, nameColumnLabel],
  );

  const defaultColumns = useMemo(
    () => buildDocumentMasterDefaultColumns(nameField),
    [nameField],
  );

  const getFieldLabel = useCallback(
    (field) => fieldOptions.find((option) => option.key === field)?.label || field,
    [fieldOptions],
  );

  const renderCell = useCallback(
    (row, field) => {
      if (field === nameField) {
        const label = row[nameField] || "—";
        const used = isRowUsed(row);
        return (
          <button
            type="button"
            className={`report-table__cell-link doc-master-name-link${
              used ? " report-table__cell-link--muted doc-master-name-link--in-use" : ""
            }`}
            title={
              used
                ? `View ${nameColumnLabel.toLowerCase()} (in use — read only)`
                : `Edit ${nameColumnLabel.toLowerCase()}`
            }
            onClick={(event) => {
              event.stopPropagation();
              onEdit?.(row);
            }}
          >
            {label}
          </button>
        );
      }

      if (field === "Status") {
        const used = isRowUsed(row);
        return (
          <div className="doc-master-status-cell">
            <span className={`badge ${used ? "bg-warning-subtle" : "bg-success"}`}>
              {used ? "In use" : "Available"}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger doc-master-delete-btn"
              onClick={(event) => onDelete?.(row, event)}
              disabled={used}
              title={used ? "In use — cannot delete" : "Delete"}
              aria-label={`Delete ${row[nameField] || nameColumnLabel.toLowerCase()}`}
            >
              <i className="fas fa-trash-alt" aria-hidden="true" />
            </button>
          </div>
        );
      }

      if (field === "SystemDateTime") {
        const formatted = utils.formatDateTimeDisplay(row.SystemDateTime);
        return formatted || "—";
      }

      const value = row[field];
      return value == null || value === "" ? "—" : String(value);
    },
    [nameField, nameColumnLabel, isRowUsed, onEdit, onDelete],
  );

  return (
    <div className="doc-master-report-wrap">
      <ReportTable
        rows={rows}
        fieldOptions={fieldOptions}
        defaultColumns={defaultColumns}
        getFieldLabel={getFieldLabel}
        renderCell={renderCell}
        pageSizeOptions={[10, 25, 50, 100]}
        frozenColumnWidthRem={14}
        hideColumnHeaderActionsFields={["Status"]}
        persistLayoutKey={layoutStorageKey}
        loading={loading}
        sortableFields={[nameField, "Abberiviation", "SystemDateTime", "CreatedBy", "Status"]}
        getSortValue={(row, field) => {
          if (field === "Status") return isRowUsed(row) ? 1 : 0;
          if (field === "SystemDateTime") {
            const t = new Date(row.SystemDateTime).getTime();
            return Number.isNaN(t) ? 0 : t;
          }
          return String(row[field] ?? "").toLowerCase();
        }}
      />
    </div>
  );
};

export default DocumentMasterReportTable;
